import { PublicReview } from '@shared/types';
import { PublicEducator } from '@cademy-package/sdk-component-types';
import { FunctionComponent, useMemo } from 'react';
import StarRating from '../StarRating';
import getVagueTimeAgo from '../../services/getVagueTimeAgo';
import CourseReviewLink from './CourseReviewLink';
import EducatorResponse from './EducatorResponse';
import ExternalLink from './ExternalLink';
import PublicMessage from './PublicMessage';
import styles from './styles.module.scss';
import VerifiedBadge from './VerifiedBadge';
import DeleteReviewButton from './DeleteReviewButton';
import { Temporal } from 'temporal-polyfill';
import { LocalDate } from '../LocalDate';

export const Review: FunctionComponent<{ review: PublicReview; educator: PublicEducator }> = ({
    review,
    educator,
}) => {
    const timeAgo = useMemo(() => {
        const now = new Date();
        const reviewDate = new Date(review.createdAt * 1000);
        const diff = Math.round((now.getTime() - reviewDate.getTime()) / 1000);
        return getVagueTimeAgo(diff);
    }, [review]);

    const isoCreatedAt = useMemo(() => {
        return new Date(review.createdAt * 1000).toISOString();
    }, [review]);

    return (
        <div
            className={styles.review}
            itemScope
            itemProp="review"
            itemType="https://schema.org/Review"
        >
            <div className={styles.header}>
                <div className={styles.headerStart}>
                    <div itemProp="author" itemScope itemType="https://schema.org/Person">
                        <span itemProp="name">{review.reviewer.name}</span>
                    </div>
                    <CourseReviewLink
                        className={styles.reviewedOn}
                        review={review}
                        educator={educator}
                    />
                    <ExternalLink className={styles.reviewedOn} review={review} />
                </div>
                <DeleteReviewButton reviewId={review.id} reviewerId={review.reviewer.id} />
            </div>
            <div className={styles.ratingRow}>
                <div>
                    <div
                        className={styles.starWrapper}
                        itemProp="reviewRating"
                        itemScope
                        itemType="https://schema.org/Rating"
                    >
                        <meta itemProp="ratingValue" content={review.rating.toString()} />
                        <StarRating rating={review.rating} />
                    </div>
                    <VerifiedBadge verified={review.verified} sourceKey={review.sourceKey} />
                </div>
                <meta itemProp="datePublished" content={isoCreatedAt} />
                <LocalDate
                    date={Temporal.Instant.fromEpochSeconds(review.createdAt)}
                    format="relative"
                    titleFormat="default"
                />
            </div>
            <PublicMessage message={review.publicMessage} />
            <EducatorResponse response={review.educatorResponse} educator={educator} />
        </div>
    );
};

export default Review;
